import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { PracticeProject } from '../../../types/practiceProjects';
import {
  getDifficultyColor,
  getStatusColor,
  formatDifficulty,
  formatStatus,
  formatRelativeTime,
} from '../../../utils/practiceProjects.utils';
import { Eye, Edit, CheckCircle, Trash2 } from 'lucide-react';

interface ProjectsTableProps {
  projects: PracticeProject[];
  onView: (projectId: string) => void;
  onEdit: (projectId: string) => void;
  onPublish: (project: PracticeProject) => void;
  onUnpublish: (project: PracticeProject) => void;
  onDuplicate: (project: PracticeProject) => void;
  onDelete: (project: PracticeProject) => void;
}

export function ProjectsTable({
  projects,
  onView,
  onEdit,
  onPublish,
  onUnpublish,
  onDuplicate,
  onDelete,
}: ProjectsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Difficulty</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Enrollments</TableHead>
          <TableHead>Updated</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.map((project) => (
          <TableRow key={project.id} className="hover:bg-muted/50">
            <TableCell>
              <div>
                <p className="font-medium">{project.title}</p>
                {project.course && (
                  <p className="text-xs text-muted-foreground">
                    {project.course.context_label}
                  </p>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge className={getDifficultyColor(project.difficulty_level)}>
                {formatDifficulty(project.difficulty_level)}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge className={getStatusColor(project.status)}>
                {formatStatus(project.status)}
              </Badge>
            </TableCell>
            <TableCell>
              {project.enrollmentCount !== undefined ? project.enrollmentCount : '-'}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatRelativeTime(project.updatedAt)}
            </TableCell>
            <TableCell>
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onView(project.id)}
                  title="View"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(project.id)}
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {project.status === 'draft' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPublish(project)}
                    title="Publish"
                  >
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(project)}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
